// @ts-nocheck
import { forwardRef, useRef, useState } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Button,
  Menu,
  ActionIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import {
  IconDownload,
  IconChevronDown,
} from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import { exportChart } from './utils/exportChart';
import type { LineChartWidgetProps, DateRange } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * LineChartWidget - Time-series line chart component
 *
 * Displays trending data over time with support for multiple lines,
 * date range filtering, and export functionality.
 */
const LineChartWidget = forwardRef<HTMLDivElement, LineChartWidgetProps>(
  (
    {
      title,
      subtitle,
      data,
      lines,
      dateRange = '30d',
      onDateRangeChange,
      height = 300,
      showGrid = true,
      showLegend = true,
      showExport = true,
      onExport,
      xAxisKey = 'date',
      xAxisLabel,
      yAxisLabel,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const chartRef = useRef<HTMLDivElement>(null);
    const [selectedRange, setSelectedRange] = useState<DateRange>(dateRange);

    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const dateRangeOptions: { value: DateRange; label: string }[] = [
      { value: '7d', label: 'Last 7 days' },
      { value: '30d', label: 'Last 30 days' },
      { value: '90d', label: 'Last 90 days' },
      { value: '1y', label: 'Last year' },
      { value: 'all', label: 'All time' },
    ];

    const handleDateRangeChange = (range: DateRange) => {
      setSelectedRange(range);
      onDateRangeChange?.(range);
    };

    const handleExport = async (format: 'png' | 'svg' | 'csv') => {
      if (onExport) {
        onExport({ format, filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart` });
      } else if (chartRef.current) {
        await exportChart(chartRef.current, data, {
          format,
          filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart`,
        });
      }
    };

    const getCurrentRangeLabel = () => {
      return dateRangeOptions.find((opt) => opt.value === selectedRange)?.label || 'Select range';
    };

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            {/* Header */}
            <Group justify="space-between" align="flex-start">
              <Box>
                {title && (
                  <Text size="lg" fw={600} style={accessibilityStyles}>
                    {title}
                  </Text>
                )}
                {subtitle && (
                  <Text size="sm" c="dimmed" style={accessibilityStyles}>
                    {subtitle}
                  </Text>
                )}
              </Box>

              <Group gap="xs">
                {/* Date range selector */}
                {onDateRangeChange && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <Button
                        variant="light"
                        size="xs"
                        rightSection={<IconChevronDown size={14} />}
                      >
                        {getCurrentRangeLabel()}
                      </Button>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Label>Date Range</Menu.Label>
                      {dateRangeOptions.map((option) => (
                        <Menu.Item
                          key={option.value}
                          onClick={() => handleDateRangeChange(option.value)}
                        >
                          {option.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                )}

                {/* Export menu */}
                {showExport && (
                  <Menu shadow="md" width={150}>
                    <Menu.Target>
                      <ActionIcon variant="light" size="lg">
                        <IconDownload size={18} />
                      </ActionIcon>
                    </Menu.Target>

                    <Menu.Dropdown>
                      <Menu.Label>Export</Menu.Label>
                      <Menu.Item onClick={() => handleExport('png')}>
                        Export as PNG
                      </Menu.Item>
                      <Menu.Item onClick={() => handleExport('svg')}>
                        Export as SVG
                      </Menu.Item>
                      <Menu.Item onClick={() => handleExport('csv')}>
                        Export as CSV
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Group>

            {/* Chart */}
            <Box ref={chartRef} style={{ width: '100%', height }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  {showGrid && (
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? theme.colors.dark[4] : theme.colors.gray[3]}
                    />
                  )}

                  <XAxis
                    dataKey={xAxisKey}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <YAxis
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                      border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                      borderRadius: theme.radius.sm,
                    }}
                    labelStyle={{ color: isDark ? theme.colors.dark[0] : theme.black }}
                  />

                  {showLegend && <Legend />}

                  {lines.map((line) => (
                    <Line
                      key={line.dataKey}
                      type="monotone"
                      dataKey={line.dataKey}
                      name={line.name}
                      stroke={line.color}
                      strokeWidth={line.strokeWidth || 2}
                      dot={line.dot !== undefined ? line.dot : false}
                      activeDot={{ r: 6 }}
                      animationDuration={1000}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

LineChartWidget.displayName = 'LineChartWidget';

export default LineChartWidget;
